/**
 * useWebPush — subscribe / unsubscribe the current user to VAPID Web Push.
 *
 * Usage:
 *   const { supported, permission, subscribed, loading, subscribe, unsubscribe }
 *     = useWebPush();
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/lib/apiBase';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function useWebPush() {
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch (_) {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, error: 'Unsupported browser' };
    setLoading(true);
    try {
      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') return { ok: false, error: 'Permission denied' };
      } else if (Notification.permission === 'denied') {
        setPermission('denied');
        return { ok: false, error: 'Permission blocked — enable in browser settings' };
      }

      const reg = await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API_BASE_URL}/webpush/public-key`);
      const appServerKey = urlBase64ToUint8Array(data.publicKey);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey,
        });
      }

      const json = sub.toJSON();
      await axios.post(`${API_BASE_URL}/webpush/subscribe`, {
        endpoint: json.endpoint,
        keys: { auth: json.keys.auth, p256dh: json.keys.p256dh },
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      console.error('[webpush] subscribe failed', e);
      return { ok: false, error: e?.message || 'Subscription failed' };
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await axios.post(`${API_BASE_URL}/webpush/unsubscribe`, { endpoint }); }
        catch (_) {}
      }
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message };
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/webpush/test`);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message };
    }
  }, []);

  return {
    supported,
    permission,
    subscribed,
    loading,
    subscribe,
    unsubscribe,
    sendTest,
    refreshStatus,
  };
}
